import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Fade,
  Tooltip,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Mic,
  CheckCircle,
  AlertCircle,
  Keyboard,
  Square,
  ArrowLeft,
} from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import ActionDialog from '../globalModals/ActionDialog';
import type { ActionType } from '../globalModals/ActionDialog';
import DictationHistory from './DictationHistory';
import OutputLanguageSelect from '../../components/OutputLanguageSelect';
import { getCurrentOutputLanguage } from '../../hooks/useOutputLanguage';
import { RootState } from '../../reducers';
import {
  closeDictationModal,
  startStreamingRecording,
  stopStreamingRecording,
  processStreamingAudioDictation,
  setDictationMode,
  resetDictation,
  DictationMode,
} from './dictationSlice';
import useLimits from '../plan/hooks/limits';

const DictationModal: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const guardLimits = useLimits();
  const { isModalOpen, status, mode, transcript, error } = useSelector(
    (state: RootState) => state.dictation
  );

  // Get custom vocabulary from team settings (same as AI.tsx)
  const { team } = useSelector((state: RootState) => state);
  const teamCustomVocabulary =
    team.selectedTeam?.fileOptions?.customVocabulary || '';

  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [editableTranscript, setEditableTranscript] = useState('');
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const recordingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Check for browser support
  const isSupported =
    'MediaRecorder' in window && 'getUserMedia' in navigator.mediaDevices;

  // Start streaming recording
  const handleStartRecording = useCallback(async () => {
    try {
      // Get current output language from centralized function
      const currentOutputLanguage = await getCurrentOutputLanguage();

      console.log(
        '🔄 Starting streaming dictation with output language:',
        currentOutputLanguage
      );

      // Start streaming recording but show as regular recording to user
      dispatch(startStreamingRecording());

      // Start the streaming process
      dispatch(
        processStreamingAudioDictation({
          options: {
            mode,
            customVocabulary: teamCustomVocabulary,
            skipLLMProcessing: false,
            languageCode: navigator.language.split('-')[0],
            outputLanguage: currentOutputLanguage,
          },
        })
      );

      // Start duration timer
      setRecordingDuration(0);
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (startRecordingError) {
      console.error(
        'Failed to start streaming recording:',
        startRecordingError
      );
    }
  }, [dispatch, mode, teamCustomVocabulary]);

  // Stop streaming recording
  const handleStopRecording = useCallback(() => {
    dispatch(stopStreamingRecording());

    // Clear duration timer
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  }, [dispatch]);

  // Request microphone permission (removed auto-start)
  useEffect(() => {
    if (isModalOpen && isSupported) {
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          setIsPermissionGranted(true);
          // Stop the stream immediately - we'll create a new one when recording
          stream.getTracks().forEach((track) => track.stop());
        })
        .catch((err) => {
          console.error('Microphone permission denied:', err);
          setIsPermissionGranted(false);
        });
    }
  }, [isModalOpen, isSupported]);

  // Handle spacebar keyboard events
  useEffect(() => {
    if (!isModalOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'AltLeft' || event.code === 'AltRight') {
        if (!event.repeat && !isSpacePressed && isPermissionGranted) {
          event.preventDefault();
          setIsSpacePressed(true);

          // Start recording if idle or success state and permission granted
          if (status === 'idle' || status === 'success') {
            if (status === 'success') {
              // Reset the dictation state first, then start recording
              dispatch(resetDictation());
              setTimeout(() => {
                handleStartRecording();
              }, 50); // Small delay to ensure state is reset
            } else {
              handleStartRecording();
            }
          }
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (
        (event.code === 'AltLeft' || event.code === 'AltRight') &&
        isSpacePressed
      ) {
        event.preventDefault();
        setIsSpacePressed(false);

        // Stop recording if currently recording
        if (status === 'recording') {
          handleStopRecording();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [
    isModalOpen,
    status,
    isPermissionGranted,
    isSpacePressed,
    handleStartRecording,
    handleStopRecording,
    dispatch,
  ]);

  // Sync editable transcript with redux transcript and handle copying
  useEffect(() => {
    if (transcript !== undefined) {
      setEditableTranscript(transcript || '');

      // Auto-copy to clipboard and show message when new transcript arrives
      if (transcript && transcript.trim() !== '') {
        navigator.clipboard
          .writeText(transcript)
          .then(() => {
            setShowCopiedMessage(true);
            setTimeout(() => {
              setShowCopiedMessage(false);
            }, 3000); // Hide after 3 seconds
          })
          .catch((err) => {
            console.error('Failed to copy to clipboard:', err);
          });
      }
    }
  }, [transcript]);

  // Close modal
  const handleClose = () => {
    // Stop recording if active
    if (status === 'recording') {
      handleStopRecording();
    }

    dispatch(closeDictationModal());
    dispatch(resetDictation());
    setIsSpacePressed(false);
    setEditableTranscript('');
    setShowCopiedMessage(false);
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle dictation mode change with guard limits
  const handleModeChange = (newMode: DictationMode) => {
    if (newMode === 'best') {
      // Guard the best mode feature
      guardLimits('dictationBestMode', () => {
        dispatch(setDictationMode(newMode));
      });
    } else {
      // Fast and answer modes are available to all plans
      dispatch(setDictationMode(newMode));
    }
  };

  // Define actions based on status
  const getActions = (): ActionType[] => {
    if (status === 'success') {
      return [
        {
          key: 'new-recording',
          label: t('dictation.modal.actions.newDictation', 'New Dictation'),
          onClick: () => {
            dispatch(resetDictation());
            setTimeout(() => {
              handleStartRecording();
            }, 100);
          },
          variant: 'contained',
          color: 'primary',
        },
      ];
    }

    if (status === 'error') {
      return [
        {
          key: 'try-again',
          label: t('dictation.modal.actions.tryAgain', 'Try Again'),
          onClick: () => {
            dispatch(resetDictation());
            setTimeout(() => {
              handleStartRecording();
            }, 100);
          },
          variant: 'contained',
          color: 'primary',
        },
      ];
    }

    // For idle, recording, and processing states - no actions
    return [];
  };

  if (!isSupported) {
    return (
      <ActionDialog
        open={isModalOpen}
        onClose={handleClose}
        title={t(
          'dictation.modal.browserNotSupported.title',
          'Browser Not Supported'
        )}
        actions={[
          {
            key: 'close',
            label: t('common.close', 'Close'),
            onClick: handleClose,
            variant: 'contained',
            color: 'primary',
          },
        ]}
        maxWidth="sm"
      >
        <Alert severity="error">
          {t(
            'dictation.modal.browserNotSupported.message',
            "Your browser doesn't support audio recording. Please use a modern browser like Chrome, Firefox, or Edge."
          )}
        </Alert>
      </ActionDialog>
    );
  }

  return (
    <ActionDialog
      open={isModalOpen}
      onClose={handleClose}
      title={t('dictation.modal.title', 'Dictation')}
      actions={getActions()}
      maxWidth="md"
      showCloseIcon
    >
      <Box>
        {/* Header with Mode Selection and History Link */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: showHistory ? 0 : 3,
          }}
        >
          {!showHistory && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={mode}
                onChange={(e) =>
                  handleModeChange(e.target.value as DictationMode)
                }
                sx={{
                  borderRadius: '50px',
                  backgroundColor: 'action.hover',
                  border: 'none',
                  '& .MuiSelect-select': {
                    py: 1,
                    borderRadius: '50px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: 'action.hover',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                      '& .MuiMenuItem-root': {
                        borderRadius: '8px',
                        mx: 1,
                        my: 0.5,
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="fast">
                  {t('dictation.modal.modes.fast', 'Fast Mode')}
                </MenuItem>
                <MenuItem value="best">
                  {t('dictation.modal.modes.best', 'Best Mode')}
                </MenuItem>
                <MenuItem value="answer">
                  {t('dictation.modal.modes.answer', 'Answer Mode')}
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {!showHistory && <OutputLanguageSelect variant="dictation" />}

          {showHistory ? (
            <IconButton
              onClick={() => setShowHistory(false)}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'text.primary',
                },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>
          ) : (
            <Typography
              component="button"
              variant="body2"
              onClick={() => setShowHistory(true)}
              sx={{
                border: 'none',
                background: 'none',
                textDecoration: 'none',
                color: 'text.secondary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {t('dictation.modal.history.title', 'History')}
            </Typography>
          )}
        </Box>

        {/* Recording Panel */}
        {!showHistory && <></>}

        {/* Permission Warning for recording */}
        {!showHistory && !isPermissionGranted && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t(
              'dictation.modal.permissionWarning',
              'Please allow microphone access to use dictation'
            )}
          </Alert>
        )}

        {/* Recording Area */}
        {!showHistory && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              mb: 3,
            }}
          >
            {status === 'idle' && (
              <Box sx={{ textAlign: 'center' }}>
                <Tooltip
                  title={t(
                    'dictation.modal.tooltips.clickToStart',
                    'Click to start recording'
                  )}
                >
                  <IconButton
                    onClick={handleStartRecording}
                    disabled={!isPermissionGranted}
                    sx={{
                      width: 120,
                      height: 120,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        transform: 'scale(1.05)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <Mic size={48} />
                  </IconButton>
                </Tooltip>
                <Box sx={{ height: 40, mt: 2 }} />
              </Box>
            )}

            {status === 'recording' && (
              <Box sx={{ textAlign: 'center' }}>
                <IconButton
                  onClick={handleStopRecording}
                  sx={{
                    width: 120,
                    height: 120,
                    backgroundColor: theme.palette.error.main,
                    color: 'white',
                    animation: 'pulse 2s infinite',
                    '&:hover': {
                      backgroundColor: theme.palette.error.dark,
                    },
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)' },
                      '50%': { transform: 'scale(1.05)' },
                      '100%': { transform: 'scale(1)' },
                    },
                  }}
                >
                  <Square
                    size={48}
                    style={{
                      color: 'white',
                      fill: 'white',
                      stroke: 'white',
                    }}
                  />
                </IconButton>
                <Box
                  sx={{
                    height: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 2,
                  }}
                >
                  <Typography variant="h6">
                    {formatDuration(recordingDuration)}
                  </Typography>
                </Box>
              </Box>
            )}

            {status === 'processing' && (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={60} />
              </Box>
            )}

            {status === 'success' && (
              <Fade in>
                <Box sx={{ width: '100%' }}>
                  <TextField
                    multiline
                    rows={6}
                    value={editableTranscript}
                    onChange={(e) => setEditableTranscript(e.target.value)}
                    placeholder={
                      editableTranscript.trim() === ''
                        ? t('dictation.modal.nothingHeard', 'Nothing heard')
                        : ''
                    }
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      sx: {
                        fontSize: '1rem',
                        lineHeight: 1.6,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.default,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      },
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: 'none',
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: theme.palette.text.secondary,
                        fontSize: '1.2rem',
                        fontWeight: 400,
                        textAlign: 'center',
                        opacity: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                      },
                    }}
                  />
                  {showCopiedMessage && (
                    <Fade in={showCopiedMessage}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        <CheckCircle
                          size={20}
                          style={{ color: theme.palette.success.main }}
                        />
                        <Typography
                          variant="body2"
                          color="success.main"
                          sx={{ fontWeight: 500 }}
                        >
                          {t(
                            'dictation.modal.copiedToClipboard',
                            'Copied to Clipboard'
                          )}
                        </Typography>
                      </Box>
                    </Fade>
                  )}
                </Box>
              </Fade>
            )}

            {status === 'error' && (
              <Box sx={{ textAlign: 'center' }}>
                <AlertCircle
                  style={{ fontSize: 48, color: theme.palette.error.main }}
                />
                <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                  {error ||
                    t(
                      'dictation.modal.error.generic',
                      'An error occurred during dictation'
                    )}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* History Panel */}
        {showHistory && (
          <Box sx={{ minHeight: 400 }}>
            <DictationHistory
              onCopy={() => {
                setShowCopiedMessage(true);
                setTimeout(() => {
                  setShowCopiedMessage(false);
                }, 3000);
              }}
            />
          </Box>
        )}

        {/* Spacebar Indicator - Always at Bottom */}
        {!showHistory && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              mt: 2,
            }}
          >
            <Chip
              icon={status !== 'recording' ? <Keyboard size={16} /> : undefined}
              label={
                status === 'recording'
                  ? t(
                      'dictation.modal.keyboard.releaseOption',
                      'Release Option to stop'
                    )
                  : t(
                      'dictation.modal.keyboard.holdOption',
                      'Hold Option to record'
                    )
              }
              variant="outlined"
              size="small"
              sx={{
                backgroundColor: isSpacePressed
                  ? 'primary.light'
                  : 'transparent',
                color: isSpacePressed
                  ? 'primary.contrastText'
                  : 'text.secondary',
                borderColor: isSpacePressed ? 'primary.main' : 'divider',
                px: 3,
                '& .MuiChip-icon': {
                  color: isSpacePressed ? 'white' : 'text.secondary',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </ActionDialog>
  );
};

export default DictationModal;
